import axios from "axios";
import { db } from "@/lib/firebase";
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp
} from "firebase/firestore";

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MINUTES = 30;

export const loginSecurityService = {
  async checkLockout(email) {
    const emailLower = email.toLowerCase();
    const lockoutRef = doc(db, "accountLockouts", emailLower);
    const lockoutDoc = await getDoc(lockoutRef);
    
    if (lockoutDoc.exists()) {
      const data = lockoutDoc.data();
      if (data.lockedUntil?.toDate) {
        const lockedUntil = data.lockedUntil.toDate();
        if (lockedUntil > new Date()) {
          const minutesLeft = Math.ceil((lockedUntil.getTime() - Date.now()) / 60000);
          return { locked: true, minutesLeft };
        }
        await deleteDoc(lockoutRef);
      }
    }
    return { locked: false };
  },
  
  async recordFailedAttempt(email) {
    const emailLower = email.toLowerCase();
    const attemptRef = doc(db, "loginAttempts", emailLower);
    const attemptDoc = await getDoc(attemptRef);
    
    let attempts = 1;
    if (attemptDoc.exists()) {
      attempts = (attemptDoc.data().count || 0) + 1;
    }
    
    await setDoc(attemptRef, {
      count: attempts,
      lastAttempt: serverTimestamp()
    }, { merge: true });
    
    if (attempts >= MAX_LOGIN_ATTEMPTS) {
      const lockoutUntil = new Date();
      lockoutUntil.setMinutes(lockoutUntil.getMinutes() + LOCKOUT_DURATION_MINUTES);
      
      await setDoc(doc(db, "accountLockouts", emailLower), {
        lockedUntil: lockoutUntil,
        reason: "Too many failed login attempts",
        lockedAt: serverTimestamp()
      });
      
      await deleteDoc(attemptRef);
      return { locked: true, minutesLeft: LOCKOUT_DURATION_MINUTES };
    }
    
    return { locked: false, attemptsRemaining: MAX_LOGIN_ATTEMPTS - attempts };
  },
  
  async clearFailedAttempts(email) {
    const emailLower = email.toLowerCase();
    const attemptRef = doc(db, "loginAttempts", emailLower);
    await deleteDoc(attemptRef);
  }
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const usersService = {
  getAll: async () => {
    const snapshot = await getDocs(collection(db, "users"));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getById: async (id) => {
    const docRef = doc(db, "users", id);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },
  
  getByEmail: async (email) => {
    const userId = email.split("@")[0];
    return usersService.getById(userId);
  },
  
  create: async (userData) => {
    const docRef = doc(collection(db, "users"));
    await setDoc(docRef, { ...userData, createdAt: serverTimestamp() });
    return { id: docRef.id, ...userData };
  },
  
  update: async (id, userData) => {
    await updateDoc(doc(db, "users", id), { ...userData, updatedAt: serverTimestamp() });
    return { id, ...userData };
  },
  
  delete: async (id) => {
    await deleteDoc(doc(db, "users", id));
    return { success: true };
  },
};

export const tasksService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "tasks"), orderBy("createdAt", "desc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getByDepartment: async (department) => {
    const snapshot = await getDocs(
      query(collection(db, "tasks"), where("department", "==", department))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getByUser: async (userId) => {
    const snapshot = await getDocs(
      query(collection(db, "tasks"), where("assignedTo", "==", userId))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getByProject: async (projectId) => {
    const snapshot = await getDocs(
      query(collection(db, "tasks"), where("projectId", "==", projectId))
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  create: async (taskData) => {
    const docRef = doc(collection(db, "tasks"));
    await setDoc(docRef, { 
      ...taskData, 
      createdAt: serverTimestamp(),
      status: taskData.status || "To Do",
      priority: taskData.priority || "Medium",
      subtasks: taskData.subtasks || [],
      comments: taskData.comments || [],
      attachments: taskData.attachments || []
    });
    return { id: docRef.id, ...taskData };
  },
  
  update: async (id, taskData) => {
    await updateDoc(doc(db, "tasks", id), { 
      ...taskData, 
      updatedAt: serverTimestamp() 
    });
    return { id, ...taskData };
  },
  
  delete: async (id) => {
    await deleteDoc(doc(db, "tasks", id));
    return { success: true };
  },

  addComment: async (taskId, comment) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (taskDoc.exists()) {
      const currentComments = taskDoc.data().comments || [];
      const newComment = {
        id: Date.now().toString(),
        ...comment,
        createdAt: new Date().toISOString()
      };
      await updateDoc(doc(db, "tasks", taskId), {
        comments: [...currentComments, newComment],
        updatedAt: serverTimestamp()
      });
      return newComment;
    }
    return null;
  },

  addSubtask: async (taskId, subtask) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (taskDoc.exists()) {
      const currentSubtasks = taskDoc.data().subtasks || [];
      const newSubtask = {
        id: Date.now().toString(),
        ...subtask,
        completed: false
      };
      await updateDoc(doc(db, "tasks", taskId), {
        subtasks: [...currentSubtasks, newSubtask],
        updatedAt: serverTimestamp()
      });
      return newSubtask;
    }
    return null;
  },

  updateSubtask: async (taskId, subtaskId, updates) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (taskDoc.exists()) {
      const currentSubtasks = taskDoc.data().subtasks || [];
      const updatedSubtasks = currentSubtasks.map(st => 
        st.id === subtaskId ? { ...st, ...updates } : st
      );
      await updateDoc(doc(db, "tasks", taskId), {
        subtasks: updatedSubtasks,
        updatedAt: serverTimestamp()
      });
      return updatedSubtasks;
    }
    return null;
  },

  deleteSubtask: async (taskId, subtaskId) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (taskDoc.exists()) {
      const currentSubtasks = taskDoc.data().subtasks || [];
      const updatedSubtasks = currentSubtasks.filter(st => st.id !== subtaskId);
      await updateDoc(doc(db, "tasks", taskId), {
        subtasks: updatedSubtasks,
        updatedAt: serverTimestamp()
      });
      return updatedSubtasks;
    }
    return null;
  }
};

export const projectsService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getById: async (id) => {
    const docRef = doc(db, "projects", id);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },
  
  create: async (projectData) => {
    const docRef = doc(collection(db, "projects"));
    await setDoc(docRef, { 
      ...projectData, 
      createdAt: serverTimestamp(),
      status: "active"
    });
    return { id: docRef.id, ...projectData };
  },
  
  update: async (id, projectData) => {
    await updateDoc(doc(db, "projects", id), { ...projectData, updatedAt: serverTimestamp() });
    return { id, ...projectData };
  },
  
  delete: async (id) => {
    await deleteDoc(doc(db, "projects", id));
    return { success: true };
  },
};

export const analyticsService = {
  getDashboardStats: async () => {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const tasksSnapshot = await getDocs(collection(db, "tasks"));
    const projects = await getDocs(query(collection(db, "projects"), where("status", "==", "active")));
    
    const users = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const tasks = tasksSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const totalUsers = users.length;
    
    const departments = {};
    users.forEach(user => {
      const dept = user.department || user.role || "Unassigned";
      departments[dept] = (departments[dept] || 0) + 1;
    });
    
    const taskStats = { pending: 0, inProgress: 0, completed: 0 };
    tasks.forEach(task => {
      const rawStatus = task.status || "";
      const status = rawStatus.toString().toLowerCase().trim();
      if (status === "pending" || status === "to do" || status === "awaiting") {
        taskStats.pending++;
      } else if (status === "in progress" || status === "inprogress" || status === "in review" || status === "blocked") {
        taskStats.inProgress++;
      } else if (status === "completed" || status === "done") {
        taskStats.completed++;
      } else if (status) {
        taskStats.inProgress++;
      } else {
        taskStats.pending++;
      }
    });
    
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const twoMonthsAgoStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - now.getDay());
    thisWeekStart.setHours(0, 0, 0, 0);
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    
    const calcDelta = (current, previous) => {
      if (!previous || previous === 0) return current > 0 ? "+100%" : null;
      const delta = ((current - previous) / previous) * 100;
      if (delta > 0) return `+${Math.round(delta)}%`;
      if (delta < 0) return `${Math.round(delta)}%`;
      return "0%";
    };
    
    const currentUsers = users.filter(u => {
      const created = u.createdAt;
      if (!created?.toDate) return false;
      return created.toDate() >= thisMonthStart;
    }).length;
    
    const previousUsers = users.filter(u => {
      const created = u.createdAt;
      if (!created?.toDate) return false;
      return created.toDate() >= lastMonthStart && created.toDate() < thisMonthStart;
    }).length;
    
    const currentProjects = projects.docs.filter(d => {
      const created = d.data().createdAt;
      if (!created?.toDate) return false;
      return created.toDate() >= thisMonthStart;
    }).length;
    
    const previousProjects = projects.docs.filter(d => {
      const created = d.data().createdAt;
      if (!created?.toDate) return false;
      return created.toDate() >= lastMonthStart && created.toDate() < thisMonthStart;
    }).length;
    
    const getCompletedInPeriod = (tasksList, startDate) => {
      return tasksList.filter(t => {
        if (t.status !== "Completed") return false;
        const updated = t.updatedAt || t.completedAt;
        if (!updated?.toDate) return false;
        return updated.toDate() >= startDate;
      }).length;
    };
    
    const completedThisMonth = getCompletedInPeriod(tasks, thisMonthStart);
    const completedLastMonth = getCompletedInPeriod(tasks, lastMonthStart) - completedThisMonth;
    
    const completedThisWeek = getCompletedInPeriod(tasks, thisWeekStart);
    const completedLastWeek = getCompletedInPeriod(tasks, lastWeekStart) - completedThisWeek;
    
    const generateTrendsData = (tasksList) => {
    const now = new Date();
    const monthlyData = [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonthDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      
      const created = tasksList.filter(t => {
        const created = t.createdAt;
        if (!created?.toDate) return false;
        const date = created.toDate();
        return date >= monthDate && date < nextMonthDate;
      }).length;
      
      const completed = tasksList.filter(t => {
        if (t.status !== "Completed") return false;
        const updated = t.updatedAt || t.completedAt;
        if (!updated?.toDate) return false;
        const date = updated.toDate();
        return date >= monthDate && date < nextMonthDate;
      }).length;
      
      monthlyData.push({
        month: monthNames[monthDate.getMonth()],
        tasks: created,
        completed: completed
      });
    }
    
    const hasRealData = monthlyData.some(m => m.tasks > 0 || m.completed > 0);
    return hasRealData ? monthlyData : null;
  };
  
  return {
    totalUsers: totalUsers,
    totalTasks: tasks.length,
    activeProjects: projects.size,
    departmentCount: Object.keys(departments).length,
    departments,
    taskStats,
    deltas: {
      users: calcDelta(currentUsers, previousUsers),
      projects: calcDelta(currentProjects, previousProjects),
      pending: calcDelta(taskStats.pending + taskStats.inProgress, taskStats.completed),
      completed: completedThisWeek > 0 || completedLastWeek > 0 ? calcDelta(completedThisWeek, completedLastWeek) : null
    },
    hasData: totalUsers > 0 || projects.size > 0 || tasks.length > 0,
    monthlyTrends: generateTrendsData(tasks)
  };
  },
  generateMonthlyTrends: (tasksList) => {
    const now = new Date();
    const monthlyData = [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonthDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      
      const created = tasksList.filter(t => {
        const created = t.createdAt;
        if (!created?.toDate) return false;
        const date = created.toDate();
        return date >= monthDate && date < nextMonthDate;
      }).length;
      
      const completed = tasksList.filter(t => {
        if (t.status !== "Completed") return false;
        const updated = t.updatedAt || t.completedAt;
        if (!updated?.toDate) return false;
        const date = updated.toDate();
        return date >= monthDate && date < nextMonthDate;
      }).length;
      
      monthlyData.push({
        month: monthNames[monthDate.getMonth()],
        tasks: created,
        completed: completed
      });
    }
    
    const hasRealData = monthlyData.some(m => m.tasks > 0 || m.completed > 0);
    return hasRealData ? monthlyData : null;
  },
};

export const intakeService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "intakes"), orderBy("createdAt", "desc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getById: async (id) => {
    const docRef = doc(db, "intakes", id);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },
  
  create: async (intakeData) => {
    const docRef = doc(collection(db, "intakes"));
    await setDoc(docRef, { 
      ...intakeData, 
      createdAt: serverTimestamp(),
      status: "pending",
      score: 0
    });
    return { id: docRef.id, ...intakeData };
  },
  
  update: async (id, intakeData) => {
    await updateDoc(doc(db, "intakes", id), { ...intakeData, updatedAt: serverTimestamp() });
    return { id, ...intakeData };
  },
  
delete: async (id) => {
    await deleteDoc(doc(db, "intakes", id));
    return { success: true };
  },
};

export const auditLogService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "auditLogs"), orderBy("createdAt", "desc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getByIntake: async (intakeId) => {
    const snapshot = await getDocs(query(
      collection(db, "auditLogs"), 
      where("intakeId", "==", intakeId),
      orderBy("createdAt", "desc")
    ));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  create: async (logData) => {
    const docRef = doc(collection(db, "auditLogs"));
    await setDoc(docRef, { 
      ...logData, 
      createdAt: serverTimestamp()
    });
    return { id: docRef.id, ...logData };
  },
  
  logIntakeCreated: async (intakeId, intakeTitle, userId, userName) => {
    const docRef = doc(collection(db, "auditLogs"));
    await setDoc(docRef, {
      intakeId,
      intakeTitle,
      action: "created",
      description: `Intake "${intakeTitle}" was created`,
      userId,
      userName,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },
  
  logIntakeUpdated: async (intakeId, intakeTitle, userId, userName, changes) => {
    const docRef = doc(collection(db, "auditLogs"));
    await setDoc(docRef, {
      intakeId,
      intakeTitle,
      action: "updated",
      description: `Intake "${intakeTitle}" was updated - ${changes}`,
      userId,
      userName,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },
  
  logIntakeDeleted: async (intakeId, intakeTitle, userId, userName) => {
    const docRef = doc(collection(db, "auditLogs"));
    await setDoc(docRef, {
      intakeId,
      intakeTitle,
      action: "deleted",
      description: `Intake "${intakeTitle}" was deleted`,
      userId,
      userName,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },
  
  logIntakeStatusChanged: async (intakeId, intakeTitle, oldStatus, newStatus, userId, userName) => {
    const docRef = doc(collection(db, "auditLogs"));
    await setDoc(docRef, {
      intakeId,
      intakeTitle,
      action: "status_changed",
      description: `Status changed from "${oldStatus}" to "${newStatus}"`,
      userId,
      userName,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },
};

export const workflowService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "workflows"), orderBy("updatedAt", "desc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getById: async (id) => {
    const docRef = doc(db, "workflows", id);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },
  
  create: async (workflowData) => {
    const docRef = doc(collection(db, "workflows"));
    await setDoc(docRef, {
      ...workflowData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: workflowData.status || "Draft",
      nodes: workflowData.nodes || [],
      connections: workflowData.connections || [],
      runCount: 0,
      successRate: 100,
      runs: []
    });
    return { id: docRef.id, ...workflowData };
  },
  
  update: async (id, workflowData) => {
    await updateDoc(doc(db, "workflows", id), {
      ...workflowData,
      updatedAt: serverTimestamp()
    });
    return { id, ...workflowData };
  },
  
  delete: async (id) => {
    await deleteDoc(doc(db, "workflows", id));
    return { success: true };
  },

  runWorkflow: async (id) => {
    const workflow = await getDoc(doc(db, "workflows", id));
    if (!workflow.exists()) return null;
    
    const runRecord = {
      id: Date.now().toString(),
      triggeredAt: new Date().toISOString(),
      triggeredBy: "Manual",
      status: "Running",
      duration: 0,
      steps: [],
      startedAt: new Date()
    };
    
    const currentRuns = workflow.data().runs || [];
    await updateDoc(doc(db, "workflows", id), {
      runs: [runRecord, ...currentRuns].slice(0, 50),
      runCount: (workflow.data().runCount || 0) + 1
    });
    
    return runRecord;
  }
};

export const calendarService = {
  getAll: async () => {
    const snapshot = await getDocs(query(collection(db, "events"), orderBy("startDate", "asc")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  
  getById: async (id) => {
    const docRef = doc(db, "events", id);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },
  
  create: async (eventData) => {
    const docRef = doc(collection(db, "events"));
    await setDoc(docRef, {
      ...eventData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...eventData };
  },
  
  update: async (id, eventData) => {
    await updateDoc(doc(db, "events", id), {
      ...eventData,
      updatedAt: serverTimestamp()
    });
    return { id, ...eventData };
  },
  
  delete: async (id) => {
    await deleteDoc(doc(db, "events", id));
    return { success: true };
  }
};

export default api;